import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/react';
import {ChevronDownIcon} from '@heroicons/react/24/outline';
import {useDispatch} from 'react-redux';
import {setSelectedResource} from '../slices/preferencesSlice';
import {MARKET_STATE_DOT_CLASS, orderMarketRows} from '../utils/marketsOverview';
import ScrollFade from './ScrollFade';

/**
 * Eyebrow-style title for the rail. Rendered by StatusHero inside its own
 * eyebrow row so it lines up with "SESSION #..." by construction.
 */
export function MarketsRailLabel() {
  return (
    <span className="hidden lg:flex shrink-0 items-center text-[11px] font-mono uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
      Market coverage
    </span>
  );
}

/**
 * MarketsRail — cross-market submission rollup on the right side of the
 * StatusHero. A one-line status (typography mirroring the headline's
 * three-tone treatment) over a per-market segment bar; clicking it opens a
 * popover listing every market (resource) in the open session, action needed
 * first. Picking a market switches the header resource selector.
 *
 * Rendered inside the hero's headline row with self-stretch, so text top and
 * bar bottom pin to the headline's exact line box. Hidden when the forecaster
 * participates in a single market (the hero already covers it).
 */
export default function MarketsRail({overview, selectedResourceId}) {
  const dispatch = useDispatch();

  if (!overview || overview.marketCount < 2) return null;

  const rows = orderMarketRows(overview.rows);

  // Lead with the actionable unit (forecasts/challenges, not markets): a
  // market is "due" whether it misses one submission or all of them. The
  // action word carries the hero's italic-teal emphasis treatment, and a
  // small market-count note below keeps the line from reading as if it
  // were about the currently selected resource.
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const status = overview.allReady
    ? {lead: 'All forecasts ', emph: 'submitted'}
    : {
        lead: `${plural(overview.dueForecastCount, 'forecast')} `,
        emph: overview.closed ? 'missed' : 'due',
      };
  const marketsNote = overview.allReady
    ? `across all ${plural(overview.marketCount, 'market')}`
    : `in ${overview.dueCount} of ${plural(overview.marketCount, 'market')}`;

  return (
    <>
      {/* Status sits in column 2 / row 1 so it vertically centers against the
          headline; the markets note drops to row 2 to line up with the chips. */}
      <Menu as="div" className="relative hidden lg:flex items-center shrink-0 lg:col-start-2 lg:row-start-1">
        <MenuButton className="-mx-2 flex items-center gap-1 rounded-lg px-2 text-xl sm:text-[1.375rem] tracking-tight leading-tight text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          <span>
            <span className="text-gray-900 dark:text-gray-100">{status.lead}</span>
            <span className="italic font-semibold text-brand-600 dark:text-brand-400">{status.emph}</span>
          </span>
          <ChevronDownIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
        </MenuButton>

        <MenuItems className="absolute right-0 top-full z-30 mt-1 w-72 origin-top-right rounded-lg bg-white dark:bg-gray-870 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white/20 focus:outline-none">
        <ScrollFade className="max-h-80 overflow-y-auto p-2">
        {rows.map((r) => {
          const isSelected = r.id === selectedResourceId;
          return (
            <MenuItem key={r.id}>
              <button
                type="button"
                onClick={() => dispatch(setSelectedResource(r.id))}
                title={`${r.ready} of ${r.total} forecast${r.total > 1 ? 's' : ''} ready — view this market`}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${MARKET_STATE_DOT_CLASS[r.state]}`} aria-hidden="true" />
                <span
                  className={`truncate ${isSelected ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-300'}`}
                >
                  {r.name}
                </span>
                {r.state !== 'ready' && (
                  <span className="ml-auto shrink-0 text-gray-400 dark:text-gray-500">
                    {r.total - r.ready} {overview.closed ? 'missed' : 'due'}
                  </span>
                )}
              </button>
            </MenuItem>
          );
        })}
        </ScrollFade>
        </MenuItems>
      </Menu>

      <span className="hidden lg:block lg:col-start-2 lg:row-start-2 text-sm leading-none text-gray-400 dark:text-gray-500">
        {marketsNote}
      </span>
    </>
  );
}
